import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Check,
  ChevronRight,
  Link2,
  Search,
  Sparkles,
  Star,
  TrendingUp,
} from "lucide-react";
import { api, type BoursoUser, type Forum } from "@/lib/api";
import { handleUpgradeError } from "@/components/UpgradeDialog";
import { BoursicoteLogo } from "@/components/BoursicoteLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useSeo } from "@/lib/useSeo";
import { cn, formatNumber, formatScore } from "@/lib/utils";

const STEPS = ["bourso", "profile", "follows", "notify"] as const;
type Step = (typeof STEPS)[number];

export default function OnboardingPage() {
  useSeo({ title: "Bienvenue", canonicalPath: "/onboarding", noindex: true });
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [stepIdx, setStepIdx] = useState(0);

  // Already onboarded (or not logged in) → don't show the wizard.
  useEffect(() => {
    if (isLoading) return;
    if (!user) navigate("/login", { replace: true });
    else if (user.onboarding_completed) navigate("/forums", { replace: true });
  }, [isLoading, user, navigate]);

  const step: Step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;

  const next = () => setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));

  async function finish() {
    try {
      await api.completeOnboarding();
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("Bienvenue sur Boursicote !");
      navigate("/forums", { replace: true });
    } catch {
      toast.error("Erreur, réessayez.");
    }
  }

  if (isLoading || !user) {
    return (
      <div className="min-h-screen grid place-items-center bg-page">
        <p className="text-sm text-foreground-muted">Chargement…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page flex flex-col">
      <header className="flex items-center justify-between p-4 border-b border-border">
        <BoursicoteLogo variant="wordmark" />
        <button
          onClick={finish}
          className="text-sm text-foreground-muted hover:text-foreground"
        >
          Passer la configuration
        </button>
      </header>

      <div className="flex-1 grid place-items-center p-4">
        <div className="w-full max-w-2xl">
          <StepDots count={STEPS.length} active={stepIdx} />

          <div className="mt-6">
            {step === "bourso" && <StepBourso />}
            {step === "profile" && <StepProfile />}
            {step === "follows" && <StepFollows />}
            {step === "notify" && <StepNotify />}
          </div>

          <div className="flex justify-between items-center mt-8">
            <button
              onClick={next}
              className="text-sm text-foreground-muted hover:text-foreground"
              disabled={isLast}
              style={{ visibility: isLast ? "hidden" : "visible" }}
            >
              Passer cette étape
            </button>
            {isLast ? (
              <Button onClick={finish}>
                <Check className="size-4" />
                Terminer
              </Button>
            ) : (
              <Button onClick={next}>
                Continuer
                <ChevronRight className="size-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepDots({ count, active }: { count: number; active: number }) {
  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "h-1.5 rounded-full transition-all",
            i === active ? "w-8 bg-primary" : "w-2 bg-surface-muted"
          )}
        />
      ))}
    </div>
  );
}

function StepShell({
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  icon: any;
  title: string;
  subtitle: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center text-center gap-2">
        <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
          <Icon className="size-6" />
        </div>
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>
        <p className="text-sm text-foreground-muted max-w-md">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

// ───────────────────── Step 1 : link Boursorama account ─────────────────────

function StepBourso() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ["bourso-search", debounced],
    queryFn: () => api.users(debounced),
    enabled: debounced.length >= 2,
  });

  const linkedId = user?.bourso_user_id ?? null;

  async function link(u: BoursoUser) {
    try {
      await api.linkBourso(u.id);
      await qc.invalidateQueries({ queryKey: ["me"] });
      await qc.invalidateQueries({ queryKey: ["follows"] });
      toast.success(`Compte @${u.username} lié et suivi.`);
    } catch {
      toast.error("Erreur lors de la liaison.");
    }
  }

  async function unlink() {
    try {
      await api.unlinkBourso();
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast.success("Compte délié.");
    } catch {
      toast.error("Erreur.");
    }
  }

  return (
    <StepShell
      icon={Link2}
      title="Avez-vous un compte Boursorama ?"
      subtitle="Liez votre profil pour suivre automatiquement votre propre activité. Optionnel."
    >
      {linkedId ? (
        <Card>
          <CardContent className="flex items-center justify-between p-4">
            <span className="text-sm text-foreground">
              Compte lié (#{linkedId}).
            </span>
            <Button size="sm" variant="outline" onClick={unlink}>
              Délier
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
            <Input
              className="pl-8"
              placeholder="Votre pseudo Boursorama…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {debounced.length >= 2 && (
            <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
              {isFetching ? (
                <p className="text-sm text-foreground-muted">Recherche…</p>
              ) : results.length === 0 ? (
                <p className="text-sm text-foreground-muted">
                  Aucun profil trouvé. Vous pourrez le faire plus tard.
                </p>
              ) : (
                results.slice(0, 8).map((u) => (
                  <button
                    key={u.id}
                    onClick={() => link(u)}
                    className="flex items-center justify-between rounded-md border border-border p-3 text-left hover:bg-surface-muted transition-colors"
                  >
                    <div>
                      <span className="font-medium text-foreground">@{u.username}</span>
                      <span className="text-xs text-foreground-muted ml-2">
                        {formatNumber(u.posts_count)} messages
                      </span>
                    </div>
                    <Link2 className="size-4 text-foreground-muted" />
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </StepShell>
  );
}

// ───────────────────── Step 2 : usage profile ─────────────────────

const PROFILES: { key: "poster" | "watcher" | "both"; label: string; desc: string }[] = [
  { key: "poster", label: "Je poste beaucoup", desc: "Vous participez activement aux forums." },
  { key: "watcher", label: "Je fais de la veille", desc: "Vous lisez et surveillez sans trop poster." },
  { key: "both", label: "Un peu des deux", desc: "Vous lisez et participez selon les sujets." },
];

function StepProfile() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const selected = user?.usage_profile ?? null;

  async function pick(key: "poster" | "watcher" | "both") {
    try {
      await api.updatePreferences({ usage_profile: key });
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch {
      toast.error("Erreur.");
    }
  }

  return (
    <StepShell
      icon={TrendingUp}
      title="Comment utilisez-vous les forums ?"
      subtitle="Cela nous aidera à personnaliser votre expérience (bientôt)."
    >
      <div className="flex flex-col gap-2">
        {PROFILES.map((p) => (
          <button
            key={p.key}
            onClick={() => pick(p.key)}
            className={cn(
              "flex items-center justify-between rounded-md border p-4 text-left transition-colors",
              selected === p.key
                ? "border-primary ring-1 ring-primary"
                : "border-border hover:bg-surface-muted"
            )}
          >
            <div>
              <p className="font-medium text-foreground">{p.label}</p>
              <p className="text-xs text-foreground-muted">{p.desc}</p>
            </div>
            {selected === p.key && <Check className="size-4 text-primary" />}
          </button>
        ))}
      </div>
    </StepShell>
  );
}

// ───────────────────── Step 3 : follow top forums ─────────────────────

function StepFollows() {
  const qc = useQueryClient();
  const { data: forums = [] } = useQuery({
    queryKey: ["forums", "hot"],
    queryFn: () => api.forums({ category: "hot", limit: 18 }),
  });
  const { data: follows = [] } = useQuery({
    queryKey: ["follows"],
    queryFn: () => api.follows(),
  });

  const followedForumIds = useMemo(
    () =>
      new Set(
        follows
          .filter((f: any) => f.target_type === "forum")
          .map((f: any) => Number(f.target_id))
      ),
    [follows]
  );

  async function toggle(f: Forum) {
    const existing = follows.find(
      (x: any) => x.target_type === "forum" && Number(x.target_id) === f.id
    );
    try {
      if (existing) {
        await api.deleteFollow(Number(existing.id));
      } else {
        await api.createFollow({ target_type: "forum", target_id: f.id });
      }
      await qc.invalidateQueries({ queryKey: ["follows"] });
    } catch (e) {
      if (handleUpgradeError(e)) return;
      toast.error("Erreur.");
    }
  }

  return (
    <StepShell
      icon={Star}
      title="Suivez quelques forums actifs"
      subtitle="Sélectionnez les valeurs qui vous intéressent. Vous serez alerté de leur activité."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto">
        {forums.map((f) => {
          const isFollowed = followedForumIds.has(f.id);
          return (
            <button
              key={f.id}
              onClick={() => toggle(f)}
              className={cn(
                "flex items-center justify-between rounded-md border p-3 text-left transition-colors",
                isFollowed
                  ? "border-primary ring-1 ring-primary"
                  : "border-border hover:bg-surface-muted"
              )}
            >
              <div className="min-w-0">
                <p className="font-medium text-sm text-foreground truncate">{f.title}</p>
                <p className="text-xs text-foreground-muted">
                  score {formatScore(f.active_score)}
                </p>
              </div>
              {isFollowed ? (
                <Check className="size-4 text-primary shrink-0" />
              ) : (
                <Star className="size-4 text-foreground-muted shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </StepShell>
  );
}

// ───────────────────── Step 4 : notifications ─────────────────────

function StepNotify() {
  const qc = useQueryClient();
  const { user } = useAuth();

  async function set(body: Parameters<typeof api.updatePreferences>[0]) {
    try {
      await api.updatePreferences(body);
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch {
      toast.error("Erreur.");
    }
  }

  return (
    <StepShell
      icon={Sparkles}
      title="Vos notifications"
      subtitle="Vous pourrez tout modifier plus tard depuis votre compte."
    >
      <div className="flex flex-col gap-2">
        <ToggleRow
          label="Récap email quotidien"
          desc="Un résumé du matin de l'activité sur vos suivis."
          value={user?.digest_enabled ?? true}
          onChange={(v) => set({ digest_enabled: v })}
        />
        <ToggleRow
          label="Newsletter produit"
          desc="Nouveautés et conseils, occasionnellement."
          value={user?.newsletter_opt_in ?? false}
          onChange={(v) => set({ newsletter_opt_in: v })}
        />
        <ToggleRow
          label="Résumés assistés par IA"
          desc="Êtes-vous à l'aise avec des synthèses générées par IA ? (bientôt disponible)"
          value={user?.llm_opt_in ?? false}
          onChange={(v) => set({ llm_opt_in: v })}
        />
      </div>
    </StepShell>
  );
}

function ToggleRow({
  label,
  desc,
  value,
  onChange,
}: {
  label: string;
  desc: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-xs text-foreground-muted max-w-sm">{desc}</p>
        </div>
        <Button size="sm" variant={value ? "default" : "outline"} onClick={() => onChange(!value)} className="shrink-0 self-start sm:self-auto">
          {value ? "Activé" : "Désactivé"}
        </Button>
      </CardContent>
    </Card>
  );
}
